import React, { useState, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import NotificationBell from "../notifications/NotificationBell";
import LocationPicker from '../multiLocation/LocationPicker';
import BackButton from "../common/BackButton";
import { formService } from "../../_services/form.service";
import { salonService } from "../../_services/salon.service";
import { newServicesAction } from "../../_actions";
import "../new-service/CatalogueTopNav.css";
import "./FormSettings.css";
import "./NewForm.css";
import LocationScope, { LOCATION_SCOPE_ALL, LOCATION_SCOPE_THIS } from "../multiLocation/LocationScope";
import { syncCatalogueAfterSave } from "../multiLocation/syncCatalogue";

/**
 * FormSettings - Component for configuring form settings
 */
const FormSettings = (props) => {
  const dispatch = useDispatch();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Locations control: a new form starts at every location (the chips show
  // it); an edit starts at this location only, so it never copies a form
  // somewhere new unless a location is ticked.
  const [locScope, setLocScope] = useState(props.location?.state?.isEditing ? LOCATION_SCOPE_THIS : LOCATION_SCOPE_ALL);
  const [editFormId, setEditFormId] = useState(null);

  // Form settings state
  const [formSettings, setFormSettings] = useState({
    formName: '',
    formType: 'automatic', // 'automatic' or 'manual'
    requireSignature: true,
    clientsCompleteForm: 'every_time', // 'every_time', 'once', 'yearly'
    formRequiredFor: 'every_service', // 'every_service', 'specific_services'
    specificServices: [] // array of commonServiceId strings
  });

  // Service selector state
  const [serviceList, setServiceList] = useState([]); // categories with services
  const [serviceSearch, setServiceSearch] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [currentFormStatus, setCurrentFormStatus] = useState(null); // track existing form's status
  const [lastSaveStatus, setLastSaveStatus] = useState(null); // track what was saved for modal text

  // Hero image state
  const [heroImage, setHeroImage] = useState(''); // current hero image URL
  const [heroImagePreview, setHeroImagePreview] = useState(''); // local preview for new upload
  const [heroUploading, setHeroUploading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  // Animated visibility for Automatic Form Settings card
  const [showAutoSettings, setShowAutoSettings] = useState(formSettings.formType === 'automatic');
  const [autoSettingsFading, setAutoSettingsFading] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const heroFileRef = useRef(null);
  const pendingHeroFile = useRef(null); // Store file for upload after form creation

  // Animate Automatic Form Settings card in/out
  useEffect(() => {
    if (formSettings.formType === 'automatic') {
      setShowAutoSettings(true);
      setAutoSettingsFading(false);
    } else {
      setAutoSettingsFading(true);
      const timer = setTimeout(() => {
        setShowAutoSettings(false);
        setAutoSettingsFading(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [formSettings.formType]);

  // Fetch service list for the service selector
  useEffect(() => {
    dispatch(newServicesAction.listServicesData({}))
      .then((res) => {
        if (res?.listServices) {
          setServiceList(res.listServices);
        }
      })
      .catch(() => {});
  }, []);

  // Get form data from location state (passed from NewForm)
  useEffect(() => {
    if (props.location?.state?.formData) {
      // Form sections data is available
      console.log('Form data received:', props.location.state.formData);
    }

    // Check if editing existing form
    if (props.location?.state?.isEditing) {
      setIsEditing(true);
      setEditFormId(props.location.state.formId);
      // An edit stays at this location unless other locations are ticked.
      setLocScope(LOCATION_SCOPE_THIS);

      // Load existing form settings, merging with defaults to ensure all keys exist
      const incomingSettings = props.location.state.formSettings || {};
      setFormSettings(prev => ({
        ...prev,
        ...incomingSettings,
        specificServices: incomingSettings.specificServices || prev.specificServices || [],
        // formName lives at the top level of location state (form.name from the API)
        // but not inside the settings object, so we need to pull it in explicitly
        formName: incomingSettings.formName || props.location.state.formName || prev.formName
      }));

      // Load isTemplate flag
      if (props.location.state.isTemplate) {
        setIsTemplate(true);
      }

      // Track current form status for contextual buttons
      if (props.location.state.formStatus) {
        setCurrentFormStatus(props.location.state.formStatus);
      }

      // Load existing hero image
      if (props.location.state.heroImage) {
        setHeroImage(props.location.state.heroImage);
      }
    }
  }, [props.location?.state]);

  // Load gallery images for the image picker
  const loadGalleryImages = () => {
    const salonId = props.user?.userInfo?.hairdresser?.salon?._id;
    if (!salonId) return;

    setGalleryLoading(true);
    salonService.getSalonSettings(salonId)
      .then((response) => {
        const data = response?.data?.responseData?.data || response?.data?.data;
        if (data) {
          const images = data.galleryImages || [];
          // Include the main salon image if it exists
          if (data.imageUrl && !images.find(img => img.url === data.imageUrl)) {
            images.unshift({ url: data.imageUrl, caption: 'Salon Image' });
          }
          setGalleryImages(images);
        }
        setGalleryLoading(false);
      })
      .catch((err) => {
        console.error('Error loading gallery images:', err);
        setGalleryLoading(false);
      });
  };

  // Navigate back to form builder
  const goBack = () => {
    props.history.push({
      pathname: '/forms/new',
      state: {
        formData: props.location?.state?.formData,
        isEditing: isEditing,
        formId: editFormId,
        formSettings: {
          ...formSettings,
          specificServices: formSettings.specificServices || []
        },
        formName: formSettings.formName || '',
        heroImage: heroImage,
        isTemplate: isTemplate
      }
    });
  };

  // Save form with a target status ('draft' or 'active')
  const saveForm = (targetStatus) => {
    const sections = props.location?.state?.formData || [];
    const name = formSettings.formName || 'Untitled Form';
    setLastSaveStatus(targetStatus);

    if (isEditing && editFormId) {
      // Update existing form via API
      formService.editForm({
        formId: editFormId,
        name: name,
        settings: formSettings,
        sections: sections,
        status: targetStatus,
        isTemplate: isTemplate,
        heroImage: heroImage
      })
      .then(() => {
        syncCatalogueAfterSave('form', String(editFormId), locScope);
        setShowSuccessModal(true);
      })
      .catch((error) => {
        console.error('Error updating form:', error);
        alert('Error updating form. Please try again.');
      });
    } else {
      // Create new form via API
      formService.createForm({
        name: name,
        status: targetStatus,
        isTemplate: isTemplate,
        settings: formSettings,
        sections: sections,
        heroImage: heroImage
      })
      .then((response) => {
        const newFormId = response?.data?.responseData?.result?._id;
        if (newFormId) syncCatalogueAfterSave('form', String(newFormId), locScope);
        // If there's a pending hero file, upload it now
        if (pendingHeroFile.current && newFormId) {
          return formService.uploadHeroImage(newFormId, pendingHeroFile.current)
            .then(() => {
              pendingHeroFile.current = null;
              setShowSuccessModal(true);
            })
            .catch((err) => {
              console.error('Hero image upload failed:', err);
              // Form was still created, just show success
              pendingHeroFile.current = null;
              setShowSuccessModal(true);
            });
        }
        setShowSuccessModal(true);
      })
      .catch((error) => {
        console.error('Error creating form:', error);
        alert('Error creating form. Please try again.');
      });
    }
  };

  // Handle continue after success
  const handleContinue = () => {
    setShowSuccessModal(false);
    props.history.push('/forms');
  };

  // Update form settings
  const updateSettings = (key, value) => {
    setFormSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Toggle a service in the specificServices selection
  const toggleServiceSelection = (commonServiceId) => {
    setFormSettings(prev => {
      const current = prev.specificServices || [];
      const exists = current.includes(commonServiceId);
      return {
        ...prev,
        specificServices: exists
          ? current.filter(id => id !== commonServiceId)
          : [...current, commonServiceId]
      };
    });
  };

  // Hero image: handle file upload
  const handleHeroImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => {
      setHeroImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);

    // If editing an existing form, upload directly
    if (isEditing && editFormId) {
      setHeroUploading(true);
      formService.uploadHeroImage(editFormId, file)
        .then((response) => {
          const data = response?.data?.responseData;
          if (data?.result?.imageUrl) {
            setHeroImage(data.result.imageUrl);
            setHeroImagePreview('');
          }
          setHeroUploading(false);
        })
        .catch((err) => {
          console.error('Error uploading hero image:', err);
          alert('Failed to upload image. Please try again.');
          setHeroImagePreview('');
          setHeroUploading(false);
        });
    } else {
      // For new forms, store the file and show preview. Upload happens after form creation.
      pendingHeroFile.current = file;
    }

    // Reset file input
    if (heroFileRef.current) {
      heroFileRef.current.value = '';
    }
  };

  // Hero image: pick from gallery
  const handlePickGalleryImage = (imageUrl) => {
    if (isEditing && editFormId) {
      // Set hero image via API
      formService.setHeroImage({ formId: editFormId, imageUrl })
        .then(() => {
          setHeroImage(imageUrl);
          setShowImagePicker(false);
        })
        .catch((err) => {
          console.error('Error setting hero image:', err);
          alert('Failed to set image. Please try again.');
        });
    } else {
      // For new forms, just store the URL — it will be sent with create payload
      setHeroImage(imageUrl);
      setHeroImagePreview('');
      pendingHeroFile.current = null;
      setShowImagePicker(false);
    }
  };

  // Hero image: remove
  const handleRemoveHeroImage = () => {
    if (isEditing && editFormId) {
      formService.deleteHeroImage({ formId: editFormId })
        .then(() => {
          setHeroImage('');
          setHeroImagePreview('');
        })
        .catch((err) => {
          console.error('Error removing hero image:', err);
        });
    } else {
      setHeroImage('');
      setHeroImagePreview('');
      pendingHeroFile.current = null;
    }
  };

  // Open image picker
  const handleOpenImagePicker = () => {
    loadGalleryImages();
    setShowImagePicker(true);
  };

  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">

        <div className="catalogue-page-wrapper">
          {/* Focused flow header — uses shared catalogue-top-nav for sticky behaviour */}
          <div className="catalogue-top-nav">
            <div className="catalogue-top-nav-container">
              <div className="catalogue-top-nav-content form-flow-header-content">
                <div className="form-flow-header-left">
                  <BackButton onClick={goBack} ariaLabel="Back" />
                  <h1 className="form-flow-header-title">{isEditing ? 'Edit Form' : 'New Form'}</h1>
                </div>
                <div className="form-flow-header-right">
                  <LocationPicker />
                  <NotificationBell />
                </div>
              </div>
            </div>
          </div>

          <div className="form-settings-wrapper">

          <div className="form-settings-container">
            {/* Hero Image Card */}
            <div className="settings-card">
              <h2 className="settings-card-title">Hero Image</h2>
              <p className="hero-image-description">
                Add an optional banner image that appears at the top of your form. You can upload a new image or pick from your business gallery.
              </p>

              {/* Current hero image preview */}
              {(heroImage || heroImagePreview) ? (
                <div className="hero-image-preview-container">
                  <img
                    src={heroImagePreview || heroImage}
                    alt="Form hero"
                    className="hero-image-preview"
                  />
                  {heroUploading && (
                    <div className="hero-image-uploading-overlay">
                      <span>Uploading...</span>
                    </div>
                  )}
                  <div className="hero-image-actions">
                    <button
                      className="hero-image-action-btn"
                      onClick={() => heroFileRef.current?.click()}
                      title="Replace image"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    </button>
                    <button
                      className="hero-image-action-btn hero-image-action-btn-danger"
                      onClick={handleRemoveHeroImage}
                      title="Remove image"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="hero-image-upload-area">
                  <div className="hero-image-upload-options">
                    <button
                      className="hero-image-upload-btn"
                      onClick={() => heroFileRef.current?.click()}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Upload Image</span>
                    </button>
                    <span className="hero-image-or">or</span>
                    <button
                      className="hero-image-gallery-btn"
                      onClick={handleOpenImagePicker}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>Pick from Business Images</span>
                    </button>
                  </div>
                </div>
              )}

              <input
                ref={heroFileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleHeroImageUpload}
              />
            </div>

            {/* Form Settings and Details */}
            <div className="settings-card">
              <h2 className="settings-card-title">Form settings and details</h2>
              <LocationScope value={locScope} onChange={setLocScope} help="Questions and settings are shared; responses stay with the location that collected them." />

              <div className="settings-field">
                <label className="settings-label">Form name</label>
                <input
                  type="text"
                  className="settings-input"
                  value={formSettings.formName}
                  onChange={(e) => updateSettings('formName', e.target.value)}
                  placeholder=""
                />
              </div>

              <div className="settings-field">
                <label className="settings-label">Form Type</label>
                <div className="settings-radio-group">
                  <label className="settings-radio-option">
                    <input
                      type="radio"
                      name="formType"
                      value="automatic"
                      checked={formSettings.formType === 'automatic'}
                      onChange={(e) => updateSettings('formType', e.target.value)}
                    />
                    <span className="radio-custom"></span>
                    <span className="radio-text">Automatic - attach forms to specific services & send form when client makes a booking</span>
                  </label>
                  <label className="settings-radio-option">
                    <input
                      type="radio"
                      name="formType"
                      value="manual"
                      checked={formSettings.formType === 'manual'}
                      onChange={(e) => updateSettings('formType', e.target.value)}
                    />
                    <span className="radio-custom"></span>
                    <span className="radio-text">Form added manually to clients profile</span>
                  </label>
                </div>
              </div>

              <div className="settings-field settings-toggle-field">
                <label className="form-toggle-switch">
                  <input
                    type="checkbox"
                    checked={formSettings.requireSignature}
                    onChange={(e) => updateSettings('requireSignature', e.target.checked)}
                  />
                  <span className="form-toggle-slider"></span>
                </label>
                <span className="toggle-label">Require Client Signature</span>
              </div>

              <div className="settings-field settings-toggle-field">
                <label className="form-toggle-switch">
                  <input
                    type="checkbox"
                    checked={isTemplate}
                    onChange={(e) => setIsTemplate(e.target.checked)}
                  />
                  <span className="form-toggle-slider"></span>
                </label>
                <div className="toggle-label-group">
                  <span className="toggle-label">Save as Template</span>
                  <span className="toggle-label-hint">Reuse this form as a starting point for new forms</span>
                </div>
              </div>
            </div>

            {/* Automatic Form Settings - animated visibility */}
            {showAutoSettings && (
              <div className={`settings-card ${autoSettingsFading ? 'settings-card-fade-out' : 'settings-card-fade-in'}`}>
                <h2 className="settings-card-title">Automatic Form Settings</h2>

                <div className="settings-field">
                  <label className="settings-label">Clients complete form</label>
                  <div className="settings-select-wrapper">
                    <select
                      className="settings-select"
                      value={formSettings.clientsCompleteForm}
                      onChange={(e) => updateSettings('clientsCompleteForm', e.target.value)}
                    >
                      <option value="every_time">Every time they book</option>
                      <option value="once">Once only</option>
                      <option value="yearly">Once per year</option>
                    </select>
                    <span className="select-arrow">▼</span>
                  </div>
                </div>

                <div className="settings-field">
                  <label className="settings-label">Form required for...</label>
                  <div className="settings-select-wrapper">
                    <select
                      className="settings-select"
                      value={formSettings.formRequiredFor}
                      onChange={(e) => updateSettings('formRequiredFor', e.target.value)}
                    >
                      <option value="every_service">Every Service</option>
                      <option value="specific_services">Specific Services</option>
                    </select>
                    <span className="select-arrow">▼</span>
                  </div>
                </div>

                {/* Service selector - shown when "Specific Services" is selected */}
                {formSettings.formRequiredFor === 'specific_services' && (
                  <div className="settings-field">
                    <label className="settings-label">Select services</label>
                    <div className="service-selector-container">
                      <div className="service-selector-search-wrapper">
                        <svg className="service-selector-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                          type="text"
                          className="service-selector-search"
                          placeholder="Search services..."
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                        />
                      </div>
                      <div className="service-selector-list">
                        {serviceList.length === 0 ? (
                          <div className="service-selector-empty">Loading services...</div>
                        ) : (
                          serviceList.map((category) => {
                            const filteredServices = (category.serviceList || []).filter(svc =>
                              svc.serviceName?.toLowerCase().includes(serviceSearch.toLowerCase())
                            );
                            if (filteredServices.length === 0) return null;
                            return (
                              <div key={category._id} className="service-category-group">
                                <div className="service-category-name">{category.categoryName}</div>
                                {filteredServices.map((svc) => {
                                  const isSelected = (formSettings.specificServices || []).includes(svc.commonServiceId);
                                  return (
                                    <label
                                      key={svc.commonServiceId}
                                      className={`service-checkbox-item ${isSelected ? 'selected' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleServiceSelection(svc.commonServiceId)}
                                      />
                                      <span className="service-checkbox-custom">
                                        {isSelected && (
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                                            <path d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </span>
                                      <span className="service-checkbox-label">{svc.serviceName}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            );
                          })
                        )}
                      </div>
                      {(formSettings.specificServices || []).length > 0 && (
                        <div className="service-selector-count">
                          {formSettings.specificServices.length} service{formSettings.specificServices.length !== 1 ? 's' : ''} selected
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Image Picker Modal */}
        {showImagePicker && (
          <div className="hero-picker-overlay" onClick={() => setShowImagePicker(false)}>
            <div className="hero-picker-modal" onClick={(e) => e.stopPropagation()}>
              <div className="hero-picker-header">
                <h3>Choose from Business Images</h3>
                <button className="hero-picker-close" onClick={() => setShowImagePicker(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="hero-picker-body">
                {galleryLoading ? (
                  <div className="hero-picker-loading">Loading images...</div>
                ) : galleryImages.length === 0 ? (
                  <div className="hero-picker-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <p>No business images found. Upload images in your Salon Settings first.</p>
                  </div>
                ) : (
                  <div className="hero-picker-grid">
                    {galleryImages.map((img, index) => (
                      <div
                        key={index}
                        className={`hero-picker-item ${heroImage === (img.url || img) ? 'selected' : ''}`}
                        onClick={() => handlePickGalleryImage(img.url || img)}
                      >
                        <img src={img.url || img} alt={img.caption || `Image ${index + 1}`} />
                        {heroImage === (img.url || img) && (
                          <div className="hero-picker-selected-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="form-success-modal-overlay">
            <div className="form-success-modal">
              <div className="form-success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="12" y="8" width="40" height="48" rx="4" stroke="#333" strokeWidth="2" fill="none"/>
                  <line x1="20" y1="20" x2="44" y2="20" stroke="#333" strokeWidth="2"/>
                  <line x1="20" y1="28" x2="44" y2="28" stroke="#333" strokeWidth="2"/>
                  <line x1="20" y1="36" x2="36" y2="36" stroke="#333" strokeWidth="2"/>
                  <circle cx="46" cy="46" r="12" fill="#4CD964" stroke="#fff" strokeWidth="2"/>
                  <path d="M41 46L44 49L51 42" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="form-success-title">
                {lastSaveStatus === 'draft'
                  ? (currentFormStatus === 'active' ? 'Form Unpublished' : 'Draft Saved')
                  : (isEditing ? 'Form Updated' : 'Form Published')}
              </h2>
              <p className="form-success-subtitle">
                {lastSaveStatus === 'draft'
                  ? (currentFormStatus === 'active'
                    ? 'Your form has been unpublished and moved back to drafts.'
                    : 'Your form has been saved as a draft. You can publish it when you\'re ready.')
                  : 'Your form is now live and ready to use.'}
              </p>
              <button className="btn btn-teal form-success-btn" onClick={handleContinue}>
                Continue
              </button>
            </div>
          </div>
        )}

          {/* Sticky Bottom Bar with Step Indicator */}
          <div className="form-flow-bottom-bar">
            <button className="form-flow-btn form-flow-btn-back" onClick={goBack}>
              Back
            </button>
            <div className="form-flow-steps">
              <span className="form-flow-step"></span>
              <span className="form-flow-step active"></span>
            </div>
            <div className="form-flow-btn-group">
              {isEditing && currentFormStatus === 'active' ? (
                <>
                  <button className="form-flow-btn form-flow-btn-draft" onClick={() => saveForm('draft')}>
                    Unpublish
                  </button>
                  <button className="form-flow-btn form-flow-btn-next" onClick={() => saveForm('active')}>
                    Update
                  </button>
                </>
              ) : (
                <>
                  <button className="form-flow-btn form-flow-btn-draft" onClick={() => saveForm('draft')}>
                    Save Draft
                  </button>
                  <button className="form-flow-btn form-flow-btn-next" onClick={() => saveForm('active')}>
                    Publish
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default withRouter(connect(mapStateToProps)(FormSettings));
